<template>
	<section class="business-section" :aria-label="t(`${I18N_NS}.business.ariaLabel`)">
		<div class="business-section__inner pc-center-1200">
			<header class="business-section__header">
				<div class="business-section__title-row">
					<img
						class="business-section__decor"
						src="../../images/title-decor-left.png"
						alt=""
						aria-hidden="true"
					/>
					<h2 class="business-section__title">{{ t(`${I18N_NS}.business.title`) }}</h2>
					<img
						class="business-section__decor"
						src="../../images/title-decor-right.png"
						alt=""
						aria-hidden="true"
					/>
				</div>
				<p class="business-section__subtitle">{{ t(`${I18N_NS}.business.subtitle`) }}</p>
			</header>

			<div class="business-section__list">
				<article
					v-for="(item, officeIndex) in list"
					:key="item.title"
					class="business-section__card"
					role="button"
					tabindex="0"
					@click="handleCardClick(item, officeIndex)"
					@keydown.enter="handleCardClick(item, officeIndex)"
				>
					<img class="business-section__image" :src="item.image" :alt="item.title" />
					<div class="business-section__body">
						<h3 class="business-section__card-title">{{ item.title }}</h3>
						<div class="business-section__contacts">
							<p
								v-for="(contact, index) in item.contacts"
								:key="`${item.title}-${contact.icon || contact.label}-${index}`"
								class="business-section__contact"
								@click.stop="handleContactClick(item, officeIndex, contact)"
							>
								<img
									v-if="contact.icon"
									class="business-section__icon"
									:src="iconMap[contact.icon]"
									alt=""
									aria-hidden="true"
								/>
								<span class="business-section__text">
									{{ contact.text || formatLegacyContact(contact) }}
								</span>
							</p>
						</div>
					</div>
				</article>
			</div>
		</div>
	</section>
</template>

<script setup>
import iconLocation from "./images/icon-location.png";
import iconId from "./images/icon-id.png";
import iconPhone from "./images/icon-phone.png";
import iconMobile from "./images/icon-mobile.png";
import iconEmail from "./images/icon-email.png";
import iconTime from "./images/icon-time.png";
import iconWhatsapp from "./images/icon-whatsapp.png";
import iconNumber from "./images/icon-number.png";
import { useI18n } from "~/composables/useI18n";
import {
	BUSINESS_PARTNER_META,
	CONTACT_TYPE_MAP,
	useAboutUsTracking,
} from "../../../composables/useAboutUsTracking";

const I18N_NS = "aboutUs";
const { t } = useI18n();
const { track } = useAboutUsTracking("pc");

defineProps({
	list: {
		type: Array,
		default: () => [],
	},
});

const iconMap = {
	location: iconLocation,
	id: iconId,
	phone: iconPhone,
	mobile: iconMobile,
	email: iconEmail,
	time: iconTime,
	whatsapp: iconWhatsapp,
	number: iconNumber,
};

const formatLegacyContact = contact => {
	if (contact.label) {
		return `${contact.label}：${contact.value || ""}`;
	}
	return contact.value || "\u00A0";
};

const getPartnerParams = (item, officeIndex, contactType) => {
	const meta = BUSINESS_PARTNER_META[officeIndex] || {};
	return {
		partner_id: meta.partner_id,
		partner_name: item.title,
		region: meta.region,
		contact_type: contactType,
	};
};

const handleCardClick = (item, officeIndex) => {
	track("business_partner_card_click", getPartnerParams(item, officeIndex));
};

const handleContactClick = (item, officeIndex, contact) => {
	const contactType = CONTACT_TYPE_MAP[contact.icon];
	if (!contactType) return;
	track("business_partner_card_click", getPartnerParams(item, officeIndex, contactType));
};
</script>

<style lang="scss" scoped>
.business-section {
	background: #ffffff;

	&__header {
		margin-bottom: 40px;
		text-align: center;
	}

	&__title-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 16px;
		margin-bottom: 12px;
	}

	&__decor {
		width: 120px;
		height: 16px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		color: #333333;
		font-size: 42px;
		font-weight: 700;
		line-height: 60px;
		white-space: nowrap;
	}

	&__subtitle {
		margin: 0;
		color: #333333;
		font-size: 32px;
		line-height: 46px;
	}

	&__list {
		border: 1px solid #efefef;
		border-radius: 20px;
		padding: 30px;
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 20px;
	}

	&__card {
		background: #ffffff;
		border: 1px solid #ebe5dc;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 8px 24px rgba(36, 24, 12, 0.04);
		cursor: pointer;
	}

	&__image {
		display: block;
		width: 100%;
		height: 180px;
		object-fit: cover;
	}

	&__body {
		padding: 20px 18px 22px;
	}

	&__card-title {
		margin: 0 0 14px;
		color: #333333;
		font-size: 24px;
		font-weight: 700;
		line-height: 1.4;
	}

	&__contacts {
		display: flex;
		flex-direction: column;
		gap: 7px;
	}

	&__contact {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin: 0;
		color: #606060;
		font-size: 16px;
		line-height: 28px;
		cursor: pointer;
	}

	&__icon {
		width: 19px;
		height: 19px;
		flex-shrink: 0;
		margin-top: 5px;
		object-fit: contain;
	}

	&__text {
		flex: 1;
		min-width: 0;
		white-space: normal;
		overflow-wrap: anywhere;
		word-break: normal;
	}
}

@media (max-width: 1024px) {
	.business-section {
		&__list {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			padding: 24px 20px;
			gap: 16px;
		}

		&__title {
			font-size: 36px;
			line-height: 1.4;
		}

		&__subtitle {
			font-size: 26px;
			line-height: 1.4;
		}

		&__card-title {
			font-size: 20px;
		}
	}
}

@media (max-width: 768px) {
	.business-section {
		&__decor {
			width: clamp(48px, 16vw, 80px);
			height: 12px;
		}

		&__title-row {
			gap: 10px;
		}

		&__title {
			white-space: normal;
			font-size: 28px;
		}

		&__subtitle {
			font-size: 20px;
		}

		&__list {
			grid-template-columns: minmax(0, 1fr);
			padding: 20px 16px;
			border-radius: 16px;
		}

		&__body {
			padding: 16px;
		}

		&__contact {
			font-size: 15px;
			line-height: 1.6;
		}
	}
}
</style>
